import styled, { css } from 'styled-components'
import {
  BaseColor,
  borderRadius,
  Breakpoint,
  breakpointEnding,
  elevation,
  linkStrong,
  rem,
  spacing,
  opaqueColor,
  themeColor,
  Typography,
  Color,
  FontSize,
} from '~theme'

interface WrapperProps {
  duration: number
  innerWidth?: number
}

export const Wrapper = styled.div<WrapperProps>`
  flex-shrink: 0;
  width: ${props => (props.innerWidth ? rem(props.innerWidth) : 'auto')};
  max-width: ${props => !props.innerWidth && rem(480)};
  text-align: left;
  border-radius: ${borderRadius};
  ${elevation(4)};
  opacity: 0;

  @media (${breakpointEnding(Breakpoint.xs)}) {
    max-width: 100%;
  }

  [data-popper-placement^='left'] & {
    transform: translateX(${spacing(-0.5)});
  }

  [data-popper-placement^='right'] & {
    transform: translateX(${spacing(0.5)});
  }

  [data-popper-placement^='bottom'] & {
    transform: translateY(${spacing(0.5)});
  }

  [data-popper-placement^='top'] & {
    transform: translateY(${spacing(-0.5)});
  }

  .tooltip-enter-active &,
  .tooltip-enter-done &,
  .tooltip-exit & {
    transition-duration: ${props => `${props.duration}ms`};
    transition-property: transform, opacity;
  }

  .tooltip-enter-active &,
  .tooltip-enter-done & {
    opacity: 1;
    transform: translate(0, 0) !important;
  }
`

export const Inner = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  width: 100%;
  transform: translateZ(0);
  padding: ${spacing(0.75)} ${spacing(1)};

  color: ${themeColor(BaseColor.shade_90)};
  background: ${themeColor(BaseColor.shade_30)};
  border-radius: inherit;
  ${Typography.labelLarge};
  ${Typography.textRegular};
`

export const IconWrapper = styled.div`
  flex-shrink: 0;
  margin-left: ${rem(-4)};
  margin-right: ${rem(8)};
  color: ${themeColor(BaseColor.shade_60)};
`

const ClickableElement = styled.div`
  flex-shrink: 0;
`

export const LinkWrapper = styled(ClickableElement)`
  margin-left: ${rem(12)};
  padding-left: ${rem(12)};
  border-left: 1px solid ${themeColor(BaseColor.shade_40)};
  ${Typography.textSemiStrong};

  &,
  & > a {
    ${linkStrong};
  }

  &:not(:last-child) {
    margin-right: ${rem(4)};
  }
`

export const ButtonWrapper = styled(ClickableElement)`
  margin: ${rem(-6)} ${rem(-12)} ${rem(-6)} auto;
`

interface ArrowProps {
  isVisible: boolean
}

export const Arrow = styled.div<ArrowProps>`
  visibility: ${props => !props.isVisible && 'hidden'};

  position: absolute;
  z-index: 1;
  width: ${rem(8)};
  height: ${rem(8)};
  background-color: ${themeColor(BaseColor.shade_30)};
  border-radius: 40%;

  [data-popper-placement^='left'] & {
    right: ${rem(-3)};
  }

  [data-popper-placement^='right'] & {
    left: ${rem(-3)};
  }

  [data-popper-placement^='top'] & {
    bottom: ${rem(-3)};
  }

  [data-popper-placement^='bottom'] & {
    top: ${rem(-3)};
  }
`

export const accentArrowStyles = css`
  width: ${rem(10)};
  height: ${rem(10)};
  background-color: transparent;
  border: none;
  border-radius: 0;

  &::before {
    content: '';
    display: block;
    width: 100%;
    height: 100%;
    background-color: ${themeColor(BaseColor.shade_30)};
    border: 1px solid ${themeColor(Color.primary)};
    transform: rotate(45deg);
    border-radius: ${rem(2)};
  }
`

interface ContainerProps {
  duration: number
  hasMobileView: boolean
  isAccent?: boolean
  zIndex: number
  $offset: number
  hoverOffset: boolean
}

const mobileViewStyles = css<ContainerProps>`
  @media (${breakpointEnding(Breakpoint.md)}) {
    overflow: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    display: flex;
    flex-direction: column;
    padding-top: ${spacing(3)};

    &::before {
      content: '';
      position: absolute;
      z-index: -1;
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
    }

    &.tooltip-enter-active,
    &.tooltip-enter-done,
    &.tooltip-exit {
      &::before {
        transition: background-color ${props => `${props.duration}ms`};
      }
    }

    &.tooltip-enter-active,
    &.tooltip-enter-done {
      &::before {
        background-color: ${opaqueColor(themeColor(BaseColor.shade_0), 75)};
      }
    }

    ${Wrapper} {
      overflow: hidden;
      width: auto;
      max-width: none;
      margin-top: auto;
      border: 0;
      border-radius: ${rem(8)} ${rem(8)} 0 0;
      box-shadow: none;

      [data-popper-placement] & {
        transform: translateY(100%);
      }
    }

    ${Inner} {
      padding-top: ${spacing(1)};
      padding-bottom: ${spacing(1)};
    }

    ${Arrow} {
      visibility: hidden;
    }
  }
`

const mobileStyles = css<ContainerProps>`
  @media (${breakpointEnding(Breakpoint.xs)}) {
    ${Wrapper} {
      max-width: calc(100vw - ${spacing()});
    }

    ${Inner} {
      padding: ${spacing(0.5)} ${spacing(0.75)};
      font-size: ${rem(FontSize.medium - 1)};
    }
  }
`

export const Container = styled.div<ContainerProps>`
  position: fixed;
  z-index: ${props => props.zIndex};
  max-width: 30rem;

  ${({ hasMobileView }) => (hasMobileView ? mobileViewStyles : mobileStyles)}

  ${({ isAccent }) =>
    isAccent &&
    css`
      ${Wrapper} {
        border: 1px solid ${themeColor(Color.primary)};
      }

      ${Arrow} {
        ${accentArrowStyles};
      }

      &[data-popper-placement^='left'] ${Arrow} {
        right: ${rem(-5)};
      }

      &[data-popper-placement^='right'] ${Arrow} {
        left: ${rem(-5)};
      }

      &[data-popper-placement^='top'] ${Arrow} {
        bottom: ${rem(-5)};
      }

      &[data-popper-placement^='bottom'] ${Arrow} {
        top: ${rem(-5)};
      }
    `};

  ${({ hoverOffset, $offset }) =>
    hoverOffset &&
    css`
      &::before {
        content: '';
        position: absolute;
      }

      &[data-popper-placement^='left']::before,
      &[data-popper-placement^='right']::before {
        top: 0;
        bottom: 0;
        width: ${rem($offset)};
      }

      &[data-popper-placement^='left']::before {
        right: ${rem(-$offset)};
      }

      &[data-popper-placement^='right']::before {
        left: ${rem(-$offset)};
      }

      &[data-popper-placement^='top']::before,
      &[data-popper-placement^='bottom']::before {
        left: 0;
        right: 0;
        height: ${rem($offset)};
      }

      &[data-popper-placement^='top']::before {
        bottom: ${rem(-$offset)};
      }

      &[data-popper-placement^='bottom']::before {
        top: ${rem(-$offset)};
      }
    `};
`
